const AUTH_BASE = '/auth';

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

async function fetchWithRetry(url, options, maxRetries = MAX_RETRIES) {
  let lastError;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await fetch(url, options);
      if (response.ok || response.status < 500) return response;
      lastError = new Error(`HTTP ${response.status}`);
      lastError.status = response.status;
    } catch (err) {
      lastError = err;
    }
    if (attempt < maxRetries - 1) {
      const delay = BASE_DELAY_MS * Math.pow(2, attempt) + Math.random() * 500;
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastError;
}

export async function loginUser(username, password) {
  const formData = new URLSearchParams();
  formData.append('username', username);
  formData.append('password', password);

  const response = await fetch(`${AUTH_BASE}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: formData,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const detail = data.detail || 'Login failed';
    const error = new Error(detail);
    error.status = response.status;
    error.detail = detail;
    throw error;
  }

  return await response.json();
}

export async function registerUser({ email, username, password, full_name, timezone }) {
  const response = await fetch(`${AUTH_BASE}/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, username, password, full_name, timezone }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Registration failed');
  }

  return await response.json();
}

export async function getCurrentUser(token) {
  const response = await fetchWithRetry(`${AUTH_BASE}/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error('Token invalid');
  }

  return await response.json();
}

export async function updateProfile(token, { full_name, timezone }) {
  const formData = new URLSearchParams();
  if (full_name != null) formData.append('full_name', full_name);
  if (timezone != null) formData.append('timezone', timezone);

  const response = await fetch(`${AUTH_BASE}/me`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Update failed');
  }

  return await response.json();
}

export async function getValidationPolicy() {
  const response = await fetch(`${AUTH_BASE.replace('/auth', '')}/api/validation-policy/public`);
  if (!response.ok) return null;
  return await response.json();
}

export async function resendVerification(username) {
  const formData = new URLSearchParams();
  formData.append('username', username);

  const response = await fetch(`${AUTH_BASE}/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: formData,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Failed to resend verification');
  }

  return await response.json();
}

export async function getUserEmail(username) {
  const formData = new URLSearchParams();
  formData.append('username', username);

  const response = await fetch(`${AUTH_BASE}/get-user-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: formData,
  });

  if (!response.ok) return null;
  const data = await response.json();
  return data.email;
}

export async function verifyEmailToken(token) {
  const response = await fetch(`${AUTH_BASE}/verify-email/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Verification failed');
  }

  return await response.json();
}

export async function changePassword(token, newPassword) {
  const response = await fetch(`${AUTH_BASE}/change-password`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ new_password: newPassword }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.detail || 'Password change failed');
  }

  return await response.json();
}

export async function ssoAzureLogin(azureToken) {
  const response = await fetch(`${AUTH_BASE}/sso/azure`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ azure_token: azureToken }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const detail = data.detail || 'Microsoft SSO login failed';
    const error = new Error(detail);
    error.status = response.status;
    error.detail = detail;
    throw error;
  }

  return response.json();
}

export async function logoutUser(token) {
  try {
    await fetch(`${AUTH_BASE}/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
  }
}

export default {
  loginUser,
  registerUser,
  getCurrentUser,
  updateProfile,
  getValidationPolicy,
  resendVerification,
  getUserEmail,
  verifyEmailToken,
  changePassword,
  logoutUser,
};
